import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiCopy,
  FiCheck,
  FiBookOpen,
  FiExternalLink,
  FiCalendar,
  FiUsers,
  FiBook,
} from "react-icons/fi";
import "./CitationViewerModal.css";

// Citation formatters for different styles
const formatCitation = {
  apa: (data) => {
    const authors = data.authors?.length > 0 
      ? data.authors.join(", ") 
      : "Unknown Author";
    const year = data.year || "n.d.";
    const title = data.title || "Untitled";
    const venue = data.venue || "";
    const doi = data.doi ? `https://doi.org/${data.doi}` : "";
    
    let citation = `${authors} (${year}). ${title}.`;
    if (venue) citation += ` ${venue}.`;
    if (doi) citation += ` ${doi}`;
    return citation;
  },
  
  mla: (data) => {
    const authors = data.authors?.length > 0 
      ? data.authors.join(", ") 
      : "Unknown Author";
    const title = data.title || "Untitled";
    const venue = data.venue || "";
    const year = data.year || "n.d.";
    
    let citation = `${authors}. "${title}."`;
    if (venue) citation += ` ${venue},`;
    citation += ` ${year}.`;
    return citation;
  },
  
  chicago: (data) => {
    const authors = data.authors?.length > 0 
      ? data.authors.join(", ") 
      : "Unknown Author";
    const title = data.title || "Untitled";
    const venue = data.venue || "";
    const year = data.year || "n.d.";
    
    let citation = `${authors}. "${title}."`;
    if (venue) citation += ` ${venue}`;
    citation += ` (${year}).`;
    return citation;
  },
  
  harvard: (data) => {
    const authors = data.authors?.length > 0 
      ? data.authors.join(", ")
      : "Unknown Author";
    const year = data.year || "n.d.";
    const title = data.title || "Untitled";
    const venue = data.venue || "";
    
    let citation = `${authors}, ${year}. ${title}.`;
    if (venue) citation += ` ${venue}.`;
    return citation;
  },
  
  bibtex: (data) => {
    const key = data.title?.toLowerCase().replace(/\s+/g, '_').slice(0, 20) || "reference";
    const authors = data.authors?.join(" and ") || "Unknown";
    const year = data.year || "";
    const title = data.title || "Untitled";
    const venue = data.venue || "";
    
    return `@article{${key}_${year},
  author = {${authors}},
  title = {${title}},
  journal = {${venue}},
  year = {${year}},
  doi = {${data.doi || ""}}
}`;
  },
};

const CITATION_STYLES = [
  { key: "apa", label: "APA" },
  { key: "mla", label: "MLA" },
  { key: "chicago", label: "Chicago" },
  { key: "harvard", label: "Harvard" },
  { key: "bibtex", label: "BibTeX" },
];

const CitationViewerModal = ({
  isOpen,
  onClose,
  title,
  citation = {},
  createdBy,
  createdAt,
}) => {
  const [activeStyle, setActiveStyle] = useState("apa");
  const [copied, setCopied] = useState(false);

  // Merge title with citation data
  const citationData = useMemo(() => ({
    title,
    authors: citation?.authors || [],
    year: citation?.year,
    venue: citation?.venue,
    doi: citation?.doi,
  }), [title, citation]);

  if (!isOpen) return null;

  const formattedCitation = formatCitation[activeStyle](citationData);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedCitation);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
const textArea = document.createElement("textarea");
      textArea.value = formattedCitation;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenDOI = () => {
    if (citationData.doi) {
      const doiUrl = citationData.doi.startsWith("http")
        ? citationData.doi
        : `https://doi.org/${citationData.doi}`;
      window.open(doiUrl, "_blank");
    }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="citation-viewer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="citation-viewer-modal"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="citation-viewer-header">
            <div className="citation-viewer-title-section">
              <div className="citation-viewer-icon">
                <FiBookOpen size={20} />
              </div>
              <div className="citation-viewer-title-info">
                <h2 className="citation-viewer-title">{title}</h2>
                <span className="citation-viewer-type">Reference / Citation</span>
              </div>
            </div>
            <button className="citation-viewer-close" onClick={onClose}>
              <FiX size={20} />
            </button>
          </header>

          {/* Reference Details */}
          <div className="citation-viewer-details">
            {citationData.authors?.length > 0 && (
              <div className="citation-detail-row">
                <div className="citation-detail-icon">
                  <FiUsers size={16} />
                </div>
                <div className="citation-detail-content">
                  <span className="citation-detail-label">Authors</span>
                  <span className="citation-detail-value">
                    {citationData.authors.join(", ")}
                  </span>
                </div>
              </div>
            )}
            
            {citationData.year && (
              <div className="citation-detail-row">
                <div className="citation-detail-icon">
                  <FiCalendar size={16} />
                </div>
                <div className="citation-detail-content">
                  <span className="citation-detail-label">Year</span>
                  <span className="citation-detail-value">{citationData.year}</span>
                </div>
              </div>
            )}
            
            {citationData.venue && (
              <div className="citation-detail-row">
                <div className="citation-detail-icon">
                  <FiBook size={16} />
                </div>
                <div className="citation-detail-content">
                  <span className="citation-detail-label">Venue / Journal</span>
                  <span className="citation-detail-value">{citationData.venue}</span>
                </div>
              </div>
            )}
            
            {citationData.doi && (
              <div className="citation-detail-row citation-detail-row--clickable" onClick={handleOpenDOI}>
                <div className="citation-detail-icon">
                  <FiExternalLink size={16} />
                </div>
                <div className="citation-detail-content">
                  <span className="citation-detail-label">DOI</span>
                  <span className="citation-detail-value citation-detail-link">
                    {citationData.doi}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Citation Styles */}
          <div className="citation-viewer-styles">
            <h3 className="citation-styles-title">Citation Format</h3>
            <div className="citation-style-tabs">
              {CITATION_STYLES.map(({ key, label }) => (
                <button
                  key={key}
                  className={`citation-style-tab ${activeStyle === key ? "citation-style-tab--active" : ""}`}
                  onClick={() => setActiveStyle(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Formatted Citation */}
          <div className="citation-viewer-output">
            <pre className={`citation-text ${activeStyle === "bibtex" ? "citation-text--code" : ""}`}>
              {formattedCitation}
            </pre>
            <button
              className={`citation-copy-btn ${copied ? "citation-copy-btn--success" : ""}`}
              onClick={handleCopy}
            >
              {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
              <span>{copied ? "Copied!" : "Copy Citation"}</span>
            </button>
          </div>

          {/* Footer with DOI button */}
          {citationData.doi && (
            <footer className="citation-viewer-footer">
              <button className="citation-open-btn" onClick={handleOpenDOI}>
                <FiExternalLink size={16} />
                <span>Open DOI Link</span>
              </button>
            </footer>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CitationViewerModal;
